import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from '../api/axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ChevronLeft, ChevronRight, FlaskConical, CalendarDays } from 'lucide-react';
import { EventGroupCard } from '../components/estabilidades/EventGroupCard';
import {
  startOfWeek, endOfWeek, addWeeks, subWeeks, format, isSameDay,
  eachDayOfInterval, isWeekend
} from 'date-fns';
import { es } from 'date-fns/locale';

function parseDate(str) {
  if (!str) return null;
  try {
    if (str.includes('/')) {
      const [d, m, y] = str.split('/');
      return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    }
    return new Date(str);
  } catch {
    return null;
  }
}

// Group a day's events by type + client, one sample counted once per group
function groupDay(dayEvents) {
  const map = new Map();
  for (const e of dayEvents) {
    const client = e.sample.client || 'Sin cliente';
    const key = `${e.type}|${client}`;
    if (!map.has(key)) map.set(key, { type: e.type, client, samples: [] });
    const g = map.get(key);
    if (!g.samples.some((s) => s.id === e.sample.id)) g.samples.push(e.sample);
  }
  return [...map.values()].sort((a, b) => (a.type === b.type ? a.client.localeCompare(b.client) : a.type === 'salida' ? -1 : 1));
}

export default function Estabilidades() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );

  useEffect(() => {
    const fetchSamples = async () => {
      try {
        const res = await axios.get('/work-samples');
        const estab = res.data.filter(s => s.destination === 'ESTABILIDADES');
        setSamples(estab);
      } catch (err) {
        console.error('Error fetching samples:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSamples();
  }, []);

  const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: currentWeekStart, end: weekEnd })
    .filter(d => !isWeekend(d));

  const events = useMemo(() => {
    const result = [];
    for (const sample of samples) {
      // Salidas de cámara
      if (Array.isArray(sample.fechas_salida_camara)) {
        for (const sc of sample.fechas_salida_camara) {
          const date = parseDate(sc.fecha);
          if (date) {
            result.push({
              type: 'salida',
              label: `Salida Camara ${sc.numero}`,
              date,
              metodo: sc.metodo,
              sample,
            });
          }
        }
      }
      // F. Informe
      const informeDate = parseDate(sample.fecha_informe_estabilidades);
      if (informeDate) {
        result.push({
          type: 'informe',
          label: 'Informe',
          date: informeDate,
          sample,
        });
      }
    }
    return result;
  }, [samples]);

  const weekEvents = useMemo(() => {
    return events.filter(e =>
      weekDays.some(day => isSameDay(day, e.date))
    );
  }, [events, weekDays]);

  const uniqueCount = (type) => new Set(weekEvents.filter(e => e.type === type).map(e => `${e.sample.id}|${e.date.toDateString()}`)).size;
  const salidaCount = uniqueCount('salida');
  const informeCount = uniqueCount('informe');

  const goToEdit = (sample) => {
    navigate('/entrada-datos', {
      state: { editSample: sample, returnTo: 'estabilidades' }
    });
  };

  const weekLabel = `${format(currentWeekStart, "d MMM", { locale: es })} - ${format(weekDays[weekDays.length - 1], "d MMM yyyy", { locale: es })}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="estabilidades-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FlaskConical className="h-6 w-6 text-indigo-600" />
            Estabilidades
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Planificador semanal de salidas de camara e informes
          </p>
        </div>
      </div>

      {/* Week Navigator */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentWeekStart(subWeeks(currentWeekStart, 1))}
              data-testid="prev-week-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Semana anterior
            </Button>

            <div className="text-center">
              <div className="flex items-center gap-2 justify-center">
                <CalendarDays className="h-5 w-5 text-indigo-600" />
                <span className="font-semibold text-lg capitalize">{weekLabel}</span>
              </div>
              <div className="flex gap-3 mt-1 justify-center">
                <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                  {salidaCount} {salidaCount === 1 ? 'salida' : 'salidas'}
                </Badge>
                <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                  {informeCount} {informeCount === 1 ? 'informe' : 'informes'}
                </Badge>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentWeekStart(addWeeks(currentWeekStart, 1))}
              data-testid="next-week-btn"
            >
              Semana siguiente <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>

          {/* Today button */}
          <div className="flex justify-center mt-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-indigo-600"
              onClick={() => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
              data-testid="today-btn"
            >
              Ir a esta semana
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Week Grid */}
      <div className="grid grid-cols-5 gap-3" data-testid="week-grid">
        {weekDays.map((day) => {
          const dayEvents = weekEvents
            .filter(e => isSameDay(e.date, day))
            .sort((a, b) => a.type === 'salida' ? -1 : 1);
          const isToday = isSameDay(day, new Date());

          return (
            <div
              key={day.toISOString()}
              className={`rounded-lg border min-h-[200px] ${
                isToday ? 'border-indigo-400 bg-indigo-50/30 ring-1 ring-indigo-200' : 'border-gray-200 bg-white'
              }`}
              data-testid={`day-col-${format(day, 'yyyy-MM-dd')}`}
            >
              {/* Day Header */}
              <div className={`px-3 py-2 border-b text-center rounded-t-lg ${
                isToday ? 'bg-indigo-100' : 'bg-gray-50'
              }`}>
                <p className="text-xs text-gray-500 uppercase">
                  {format(day, 'EEEE', { locale: es })}
                </p>
                <p className={`text-lg font-bold ${isToday ? 'text-indigo-700' : 'text-gray-800'}`}>
                  {format(day, 'd')}
                </p>
              </div>

              {/* Events */}
              <div className="p-2 space-y-2">
                {dayEvents.length === 0 && (
                  <p className="text-xs text-gray-300 text-center py-4 italic">
                    Sin eventos
                  </p>
                )}
                {groupDay(dayEvents).map((group) => (
                  <EventGroupCard
                    key={`${group.type}-${group.client}`}
                    group={group}
                    onOpen={goToEdit}
                    testId={`event-card-${format(day, 'yyyy-MM-dd')}-${group.type}-${group.client.replace(/\W+/g, '_')}`}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {samples.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <FlaskConical className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No hay muestras de Estabilidades registradas</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate('/entrada-datos')}
            >
              Crear muestra
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
